import type { BudgetSource, BudgetState } from '@dbrain/shared';
import { Badge, Button, Icons, PortalPanel, Progress } from '@dbrain/ui';
import { useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isApiError } from '../../api/client.js';
import { formatTokens } from '@dbrain/app-core/format';
import { ErrorAlert } from '../ErrorAlert.js';

/**
 * The budget pieces of Kullanım ve limitler (LimitlerOzet, LimitlerUyeler,
 * 06 §23.5): a token box that groups the digits as they are typed and says
 * the short value next to it, the source chip, today's meter and the small
 * editor that opens under a budget.
 */

/** "30.000.000 = 30 Mn": digits only, grouped while typing; empty means "no value of its own". */
export function TokenInput({ value, onChange, placeholder, label, testId, invalid }: { value: string; onChange: (digits: string) => void; placeholder?: string; label: string; testId?: string; invalid?: boolean }) {
  const { i18n } = useTranslation();
  const digits = value.replace(/\D/g, '');
  const grouped = digits ? Number(digits).toLocaleString(i18n.language) : '';
  return (
    <span className="token-input">
      <input
        className="ui-input"
        inputMode="numeric"
        autoComplete="off"
        aria-label={label}
        aria-invalid={invalid || undefined}
        value={grouped}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 13))}
        data-testid={testId}
      />
      {digits ? (
        <span className="token-input__short" aria-hidden="true">
          = {formatTokens(Number(digits), i18n.language)}
        </span>
      ) : null}
    </span>
  );
}

const SOURCE_TONE: Record<BudgetSource, 'info' | 'neutral' | 'accent'> = { personal: 'accent', domain: 'info', organization: 'neutral', platform: 'neutral' };

/** Where a budget comes from: Özel, Domain, Firma or Platform. */
export function SourceChip({ source }: { source: BudgetSource }) {
  const { t } = useTranslation();
  return (
    <Badge tone={SOURCE_TONE[source]} className={`source-chip source-chip--${source}`}>
      {t(`admin.usage.source.${source}`)}
    </Badge>
  );
}

/** Today against the budget: the bar, "17,3 Mn / 20 Mn" and Doldu (100%+) or Yaklaştı (80–99%) (D-97). */
export function TodayMeter({ used, budget, state }: { used: number; budget: number; state: BudgetState }) {
  const { t, i18n } = useTranslation();
  const percent = Math.min(100, Math.round((used / Math.max(1, budget)) * 100));
  return (
    <span className={`today-meter today-meter--${state}`} data-testid="member-meter" data-state={state}>
      <Progress value={percent} max={100} label={t('usage.percent', { percent })} tone={state === 'full' ? 'danger' : state === 'near' ? 'warning' : 'running'} />
      <span className="today-meter__numbers">
        {formatTokens(used, i18n.language)} / {formatTokens(budget, i18n.language)}
      </span>
      {state === 'full' ? <Badge tone="danger">{t('admin.usage.state.full')}</Badge> : state === 'near' ? <Badge tone="warning">{t('admin.usage.state.near')}</Badge> : null}
    </span>
  );
}

/**
 * The editor under a budget (LimitlerUyeler.dc.html): "Varsayılan · 20 Mn (İnsan Kaynakları, firmanın
 * değeri)" or one of its own. It is a <details> menu, so Escape and a click elsewhere close it.
 */
export function BudgetEditor({
  trigger,
  title,
  subtitle,
  inheritLabel,
  ownLabel,
  current,
  busy,
  error,
  onSave,
  testId,
  triggerLabel,
  triggerClassName = 'budget-editor__trigger',
  pencil = true,
}: {
  trigger: ReactNode;
  title: string;
  subtitle?: string;
  inheritLabel: string;
  ownLabel: string;
  /** the value of its own now; null: the inherited one applies */
  current: number | null;
  busy: boolean;
  error: unknown;
  onSave: (value: number | null) => Promise<unknown>;
  testId?: string;
  triggerLabel: string;
  /** the trigger's look: the budget with a pencil (default) or a button of its own */
  triggerClassName?: string;
  pencil?: boolean;
}) {
  const { t } = useTranslation();
  const name = useId();
  const menu = useRef<HTMLDetailsElement>(null);
  const [own, setOwn] = useState(current !== null);
  const [digits, setDigits] = useState(current !== null ? String(current) : '');
  const reset = () => {
    setOwn(current !== null);
    setDigits(current !== null ? String(current) : '');
  };
  const close = () => {
    if (menu.current) menu.current.open = false;
  };
  const valid = !own || Number(digits) >= 1000;
  return (
    <details ref={menu} className="ui-menu budget-editor" data-testid={testId}>
      <summary
        className={triggerClassName}
        aria-label={triggerLabel}
        title={triggerLabel}
        onClick={() => {
          // opening starts from the current value; before the toggle event, which can come after the first keystroke
          if (!menu.current?.open) reset();
        }}
      >
        {trigger}
        {pencil ? <Icons.edit size={14} className="budget-editor__pencil" /> : null}
      </summary>
      {/* D-218: drawn over the page, so a table cell or the next rows cannot cut it off */}
      <PortalPanel menu={menu} align="right" className="budget-editor__panel" role="group" aria-label={title} data-testid="budget-editor-panel">
        <p className="budget-editor__title">{title}</p>
        {subtitle ? <p className="budget-editor__subtitle">{subtitle}</p> : null}
        <label className="ui-check budget-editor__choice">
          <input type="radio" name={name} checked={!own} onChange={() => setOwn(false)} data-testid="budget-editor-inherit" />
          <span>{inheritLabel}</span>
        </label>
        <label className="ui-check budget-editor__choice">
          <input type="radio" name={name} checked={own} onChange={() => setOwn(true)} data-testid="budget-editor-own" />
          <span>{ownLabel}</span>
        </label>
        {own ? <TokenInput value={digits} onChange={setDigits} label={ownLabel} testId="budget-editor-value" invalid={!valid} /> : null}
        {isApiError(error, 'validation_failed') && error.fields?.some((f) => f.code === 'too_big') ? (
          <p className="budget-editor__error" role="alert" data-testid="budget-editor-too-big">
            {t('admin.usage.editor.tooBig')}
          </p>
        ) : (
          <ErrorAlert error={error} />
        )}
        <div className="budget-editor__actions">
          <Button variant="secondary" size="sm" onClick={close} data-testid="budget-editor-cancel">
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            busy={busy}
            disabled={!valid}
            onClick={() => {
              void onSave(own ? Number(digits) : null).then(close, () => undefined);
            }}
            data-testid="budget-editor-save"
          >
            {t('common.save')}
          </Button>
        </div>
      </PortalPanel>
    </details>
  );
}
